import React from "react";
import { Link } from "react-router-dom";
import {
  Users, CalendarClock, PlaneTakeoff, FileText, CreditCard,
  Bell, TrendingUp, Receipt, Globe, AlarmClock, MessageSquare, Bookmark, Link as LinkIcon, Rss, Code, ArrowUpRight,
} from "lucide-react";
import { moduleIcon } from "@/components/modules/moduleUtils";

const ICON_MAP = {
  Users, CalendarClock, PlaneTakeoff, FileText, CreditCard,
  Bell, TrendingUp, Receipt, Globe, AlarmClock, MessageSquare, Bookmark,
};

const CUSTOM_ICONS = { iframe: Code, rss: Rss, text: FileText, link_list: LinkIcon };

/**
 * Body du widget chef — utilisé en dashboard (readOnly) ET en preview settings.
 * Reçoit `widget` (catalog entry) et `data` (payload live).
 * Support widgets built-in ET custom (iframe/rss/text/link_list).
 */
export function ChefWidgetLiveBody({ widget, data }) {
  const isCustom = widget.is_custom === true || widget.key?.startsWith("custom_");
  const isModule = widget.is_module === true;
  const Icon = isCustom ? (CUSTOM_ICONS[widget.type] || Bookmark) : isModule ? moduleIcon(widget.icon) : (ICON_MAP[widget.icon] || FileText);
  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-8 w-8 border border-[#DEE2E6] flex items-center justify-center flex-shrink-0 ${isCustom ? "bg-[#F3F4F6]" : isModule ? "bg-[#0A0A0A] border-[#0A0A0A]" : ""}`}>
          <Icon className={`h-4 w-4 ${isModule ? "text-[#FFD700]" : "text-[#002FA7]"}`} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-semibold flex-1 truncate">{widget.label}</div>
        {isCustom && <span className="text-[9px] uppercase font-bold text-[#7C3AED] bg-[#EDE9FE] px-1.5 py-0.5">Custom</span>}
        {isModule && <span className="text-[9px] uppercase font-bold text-[#0A0A0A] bg-[#FFD700] px-1.5 py-0.5" data-testid={`module-widget-badge-${widget.key}`}>Module</span>}
      </div>
      <div className="flex-1 flex flex-col justify-center min-h-0 overflow-hidden">
        {isCustom ? <CustomBody widget={widget} data={data} /> : isModule ? <ModuleKpiBody widget={widget} data={data} /> : <ChefWidgetBody widget={widget} data={data} />}
      </div>
    </>
  );
}

/** Indicateur d'un module de l'Atelier (Phase 87) : valeur + lien vers le module. */
function ModuleKpiBody({ widget, data }) {
  const v = data?.value;
  const type = data?.kpi_type || widget.kpi_type;
  const shown = v == null ? "—" : type === "sum" || type === "avg" ? Number(v).toLocaleString("fr-FR") : v;
  return (
    <div data-testid={`module-widget-${widget.key}`}>
      <div className={`font-display text-4xl font-black tracking-tighter ${type === "overdue" && v ? "text-[#E60000]" : ""}`}>{shown}</div>
      <div className="text-[10px] text-[#868E96] mt-1 truncate">{widget.entity_label}{type === "overdue" ? " · en retard" : type === "sum" ? " · total" : type === "avg" ? " · moyenne" : ""}</div>
      <Link to={data?.url || widget.url || `/chef/modules/${widget.module_key}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold mt-2 text-[#002FA7] hover:underline" data-testid={`module-widget-link-${widget.key}`}>
        Ouvrir le module <ArrowUpRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

function CustomBody({ widget, data }) {
  const type = widget.type;
  // RSS : fetch on demand (data from /widgets/data ne contient pas les items pour éviter la latence)
  const [rssItems, setRssItems] = React.useState(null);
  React.useEffect(() => {
    if (type !== "rss") return;
    let cancelled = false;
    // Import direct pour éviter cycle
    import("@/api").then(({ api }) => {
      api.get(`/chef/custom-widgets/${widget.key}/data`)
        .then((r) => { if (!cancelled) setRssItems(r.data.items || []); })
        .catch(() => { if (!cancelled) setRssItems([]); });
    });
    return () => { cancelled = true; };
  }, [widget.key, type]);

  const config = data?.config || {};
  if (type === "iframe" && config.url) {
    return (
      <iframe
        src={config.url}
        title={widget.label}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-same-origin allow-forms"
        referrerPolicy="no-referrer"
        data-testid="custom-widget-iframe"
      />
    );
  }
  if (type === "rss") {
    const items = rssItems ?? [];
    return (
      <ul className="space-y-1 text-xs overflow-auto" data-testid="custom-widget-rss">
        {rssItems === null ? (
          <li className="text-[#868E96]">Chargement du flux…</li>
        ) : items.length === 0 ? (
          <li className="text-[#868E96]">Flux vide ou inaccessible.</li>
        ) : items.map((it, i) => (
          <li key={i} className="border-b border-[#DEE2E6] last:border-b-0 pb-1">
            <a href={it.link} target="_blank" rel="noreferrer" className="font-semibold hover:text-[#002FA7] line-clamp-2">
              {it.title}
            </a>
            {it.pubDate && <div className="text-[10px] text-[#868E96]">{it.pubDate.substring(0, 25)}</div>}
          </li>
        ))}
      </ul>
    );
  }
  if (type === "text" && config.content) {
    return (
      <div className="text-sm overflow-auto text-[#495057]" data-testid="custom-widget-text"
           dangerouslySetInnerHTML={{ __html: config.content }} />
    );
  }
  if (type === "link_list") {
    const links = config.links || [];
    return (
      <ul className="space-y-1 text-xs" data-testid="custom-widget-links">
        {links.map((l, i) => (
          <li key={i}>
            <a href={l.url} target="_blank" rel="noreferrer" className="font-semibold text-[#002FA7] hover:underline">
              → {l.label}
            </a>
          </li>
        ))}
      </ul>
    );
  }
  return <div className="text-xs text-[#868E96]">Configuration incomplète.</div>;
}

function ChefWidgetBody({ widget, data }) {
  if (!data) return <div className="text-2xl font-display font-black text-[#DEE2E6]">—</div>;
  switch (widget.key) {
    case "salaries":
    case "planning_today":
    case "conges_pending":
    case "documents":
    case "notifications":
    case "helper_unread":
    case "bulletins":
    case "minisite_bookings":
      return <div className="font-display text-4xl font-black tracking-tighter">{data.value ?? 0}</div>;

    case "subscription":
      return (
        <div>
          <div className="font-display text-2xl font-black tracking-tight uppercase">{data.status || "—"}</div>
          <div className="text-xs text-[#868E96] mt-1">Plan {data.plan || "—"}</div>
          {data.days_until_due != null && (
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mt-2 text-[#002FA7]">
              Échéance dans {data.days_until_due} j
            </div>
          )}
        </div>
      );

    case "finance_ca":
      return (
        <div>
          <div className="font-display text-3xl font-black tracking-tighter">
            {data.value != null ? `${data.value.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €` : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#868E96] mt-1">
            Mois {String(data.month).padStart(2, "0")}/{data.year}
          </div>
        </div>
      );

    case "next_conges":
      return (
        <div className="space-y-1">
          {(data.items || []).length === 0 ? (
            <div className="text-sm text-[#868E96]">Aucun congé à venir</div>
          ) : (
            data.items.map((c, i) => (
              <div key={i} className="text-xs text-[#495057]">
                <b>{c.salarie_nom} {c.salarie_prenom}</b> · {new Date(c.date_debut).toLocaleDateString("fr-FR")}
              </div>
            ))
          )}
        </div>
      );

    case "renewal_countdown":
      return (
        <div>
          <div className="font-display text-4xl font-black tracking-tighter" style={{ color: (data.days ?? 30) < 7 ? "#E60000" : "#0A0A0A" }}>
            {data.days != null ? `${data.days} j` : "—"}
          </div>
          {data.due && <div className="text-xs text-[#868E96] mt-1">Le {new Date(data.due).toLocaleDateString("fr-FR")}</div>}
        </div>
      );

    default:
      return <div className="text-xs text-[#868E96]">—</div>;
  }
}
